import './CocktailScreen.scss';

import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { AddCategoryForm } from '../components/AddCategoryForm';
import BannerSelector from '../components/BannerSelector';
import CategoryComponent from '../components/CategoryComponent';
import { PrefillButton } from '../components/PrefillButton';

import useMenuContext from '../hooks/useMenuContext';
import useSearchPoolContext from '../hooks/useSearchPoolContext';
import { BEVERAGE_TYPES } from '../lib/constants';
import { constructArrayFromIdsAndHash } from '../lib/constructArrayFromIdsAndHash';

export default function CategoriesScreen({ categories, type }) {
  const { t } = useTranslation();

  const {
    addCategory,
    updateCategory,
    moveCategory,
    deleteCategory,

    addBeverage,
    updateBeverage,
    deleteBeverage,
    moveBeverageAcrossCategories,

    menuItems,

    drinkBanners,
    cocktailBanners,
  } = useMenuContext();

  const { categoriesSearchPool } = useSearchPoolContext();

  const banners = useMemo(() => {
    if (type === BEVERAGE_TYPES.DRINK) return drinkBanners;
    if (type === BEVERAGE_TYPES.COCKTAIL) return cocktailBanners;
  }, [type, drinkBanners, cocktailBanners]);

  const resolveCategoryDropIndex = event => {
    const rows = Array.from(
      event.currentTarget.querySelectorAll('[data-category-index]')
    );

    if (!rows.length) return 0;

    const pointerY = event.clientY;

    for (const row of rows) {
      const rect = row.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      if (pointerY < midpoint) {
        return Number(row.dataset.categoryIndex);
      }
    }

    return rows.length;
  };

  const handleCategoryDragOver = event => {
    const payloadRaw = event.dataTransfer.getData('application/x-brown-forman-dnd');

    if (!payloadRaw) return;

    try {
      const payload = JSON.parse(payloadRaw);
      if (payload.dragType !== 'category') return;

      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    } catch (e) {
      // Ignore malformed drag payloads from other sources.
    }
  };

  const handleCategoryDrop = async event => {
    const payloadRaw = event.dataTransfer.getData('application/x-brown-forman-dnd');

    if (!payloadRaw) return;

    try {
      const payload = JSON.parse(payloadRaw);
      if (payload.dragType !== 'category') return;

      event.preventDefault();

      const sourceIndex = Number(payload.sourceIndex);
      const destinationIndexRaw = resolveCategoryDropIndex(event);
      const destinationIndex =
        sourceIndex < destinationIndexRaw
          ? destinationIndexRaw - 1
          : destinationIndexRaw;

      if (sourceIndex === destinationIndex) return;

      await moveCategory(sourceIndex, destinationIndex, type);
    } catch (e) {
      console.error(e);
      window.alert(t('itemUpdateError_plural'));
    }
  };

  return (
    <div className="container">
      {type === BEVERAGE_TYPES.DRINK && (
        <div className="row mb-4">
          <div className="col-12">
            <PrefillButton />
          </div>
        </div>
      )}

      <div onDragOver={handleCategoryDragOver} onDrop={handleCategoryDrop}>
        {categories.length ? (
          categories.map((category, index) => {
            const canBeDragged = category.movable;

            return (
              <div className="row" data-category-index={index} key={category.id}>
                <CategoryComponent
                  category={category}
                  onUpdate={updateCategory}
                  onDelete={deleteCategory}
                  onBeverageAdd={addBeverage}
                  onBeverageUpdate={updateBeverage}
                  onBeverageDelete={deleteBeverage}
                  onBeverageMove={moveBeverageAcrossCategories}
                  beverages={constructArrayFromIdsAndHash(
                    category.beverageIds,
                    menuItems
                  )}
                  dragHandleProps={
                    canBeDragged
                      ? {
                          draggable: true,
                          onDragStart: event => {
                            event.stopPropagation();
                            event.dataTransfer.effectAllowed = 'move';
                            const payload = JSON.stringify({
                              dragType: 'category',
                              sourceIndex: index,
                              categoryId: String(category.id),
                            });
                            event.dataTransfer.setData(
                              'application/x-brown-forman-dnd',
                              payload
                            );
                            event.dataTransfer.setData('text/plain', payload);
                          },
                        }
                      : {}
                  }
                  canBeDragged={canBeDragged}
                />
              </div>
            );
          })
        ) : (
          <p className="text-center">
            {type === BEVERAGE_TYPES.DRINK
              ? t('emptyDrinksList')
              : t('emptyCocktailsList')}
          </p>
        )}

        {/*
           This silly conditional rendering below may look redundant,
            but it's an important hotfix.

           For some reason if we're using the same component for both types,
           useSearch inside keeps the searchPool from the first type we visited
           and uses it for both cases. It's definitely not what we want.
         */}

        {type === BEVERAGE_TYPES.DRINK && (
          <AddCategoryForm
            type={type}
            onSubmit={addCategory}
            searchPool={categoriesSearchPool[type]}
          />
        )}

        {type === BEVERAGE_TYPES.COCKTAIL && (
          <AddCategoryForm
            type={type}
            onSubmit={addCategory}
            searchPool={categoriesSearchPool[type]}
          />
        )}
      </div>

      {Boolean(categories.length) && (
        <BannerSelector type={type} banners={banners} />
      )}
    </div>
  );
}
